import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Loader2, ShieldCheck, ShieldOff, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact Settings card to manage the Financials PIN.
 *
 * Change Feb 2026: to set/rotate/remove the PIN the user must enter
 * their *login password* — not just the existing PIN. Stops an
 * employee from quietly rotating it when the owner walks away from an
 * unlocked terminal.
 */
export default function FinancialsPinCard({ businessSlug, isDark }) {
  const [status, setStatus] = useState({ loading: true, hasPin: false });
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await axios.get(`${BASE_API}/b/${businessSlug}/financials/pin/status`);
      setStatus({ loading: false, hasPin: !!r.data.has_pin });
    } catch {
      setStatus({ loading: false, hasPin: false });
    }
  };

  useEffect(() => {
    if (businessSlug) refresh();
  }, [businessSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!/^\d{4,8}$/.test(pin)) {
      toast.error("PIN must be 4–8 digits.");
      return;
    }
    if (pin !== confirmPin) {
      toast.error("PINs don't match.");
      return;
    }
    if (!password) {
      toast.error("Enter your login password to confirm.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${BASE_API}/b/${businessSlug}/financials/pin/set`, {
        pin,
        user_password: password,
      });
      toast.success(status.hasPin ? "PIN updated." : "PIN set. Financials are now locked.");
      setPin("");
      setConfirmPin("");
      setPassword("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save PIN.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!password) {
      toast.error("Enter your login password to remove protection.");
      return;
    }
    if (!window.confirm("Remove the Financials PIN? Anyone logged in will be able to see the numbers.")) return;
    setBusy(true);
    try {
      await axios.request({
        method: "DELETE",
        url: `${BASE_API}/b/${businessSlug}/financials/pin`,
        data: { user_password: password },
      });
      toast.success("Protection removed.");
      setPassword("");
      setPin("");
      setConfirmPin("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove PIN.");
    } finally {
      setBusy(false);
    }
  };

  if (status.loading) {
    return (
      <Card className={isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"} data-testid="financials-pin-card">
        <CardContent className="p-3 flex items-center gap-2 text-xs text-slate-500">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"} data-testid="financials-pin-card">
      <CardHeader className={`border-b ${isDark ? "border-slate-700" : "border-slate-200"} py-2.5 px-4`}>
        <CardTitle className={`flex items-center gap-2 text-sm ${isDark ? "text-white" : "text-slate-800"}`}>
          {status.hasPin
            ? <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
            : <ShieldOff className="w-3.5 h-3.5 text-slate-400" />}
          Financials PIN
          {status.hasPin && (
            <span className="text-[9px] uppercase tracking-wider font-semibold text-emerald-500 ml-auto">Active</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 space-y-2.5">
        <p className={`text-[11px] leading-snug ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          Hides the Financials page from anyone without the PIN. Changing or removing it requires your login password.
        </p>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider">{status.hasPin ? "New PIN" : "Set PIN"}</Label>
            <Input
              type="password"
              inputMode="numeric"
              pattern="\d*"
              maxLength={8}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              placeholder="4–8 digits"
              className="font-mono h-8 text-sm"
              data-testid="financials-pin-new"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider">Confirm</Label>
            <Input
              type="password"
              inputMode="numeric"
              pattern="\d*"
              maxLength={8}
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              placeholder="Repeat"
              className="font-mono h-8 text-sm"
              data-testid="financials-pin-confirm"
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider">Your login password</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your account password"
            className="h-8 text-sm"
            data-testid="financials-pin-password"
            autoComplete="current-password"
          />
        </div>

        <div className="flex flex-wrap gap-2 pt-0.5">
          <Button
            type="button"
            size="sm"
            onClick={save}
            disabled={busy || pin.length < 4 || !password}
            className="flex-1 h-8 bg-amber-500 hover:bg-amber-600 text-white text-xs"
            data-testid="financials-pin-save"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Lock className="w-3.5 h-3.5 mr-1.5" />}
            {status.hasPin ? "Update" : "Set PIN"}
          </Button>
          {status.hasPin && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={remove}
              disabled={busy || !password}
              className="h-8 text-red-500 hover:text-red-600 text-xs"
              data-testid="financials-pin-remove"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
